import React, { useEffect, useMemo, useState } from "react";

import api from "../services/api";
import { endpoints } from "../services/endpoints";
import { normalizeUser } from "../utils/user";
import { normalizeRole } from "../utils/roles";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? normalizeUser(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export default function Settings() {
  const [user, setUser] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;

    async function loadUser() {
      const stored = readStoredUser();
      if (!stored?.id) return;

      setLoading(true);
      try {
        const res = await api.get(endpoints.users.byId(stored.id));
        const payload = res.data?.data ?? res.data ?? {};
        const freshUser = normalizeUser({
          ...stored,
          ...payload,
          company: payload?.company ?? stored?.company,
        });

        if (alive) {
          setUser(freshUser);
          localStorage.setItem("user", JSON.stringify(freshUser));
        }
      } catch {
        if (alive) setUser(stored);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadUser();
    return () => {
      alive = false;
    };
  }, []);

  const role = useMemo(() => normalizeRole(user?.role) ?? "-", [user]);
  const companyName =
    user?.company?.name ?? user?.companyName ?? user?.company_name ?? "-";

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-semibold">Configuración</h1>
        <p className="text-gray-600 mt-1">Datos del usuario.</p>
      </div>

      <section className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-xl font-semibold mb-5">Datos del usuario</h2>

        <div className="grid grid-cols-[minmax(120px,180px)_1fr] gap-y-4 gap-x-4 text-sm md:text-base">
          <div className="text-gray-600">Nombre</div>
          <div className="text-right font-medium break-words">{user?.name ?? "-"}</div>

          <div className="text-gray-600">Identificación</div>
          <div className="text-right font-medium break-words">
            {user?.identification ?? "-"}
          </div>

          <div className="text-gray-600">Email</div>
          <div className="text-right font-medium break-words">{user?.email ?? "-"}</div>

          <div className="text-gray-600">Teléfono</div>
          <div className="text-right font-medium break-words">{user?.phone ?? "-"}</div>

          <div className="text-gray-600">Rol</div>
          <div className="text-right font-medium break-words">{role}</div>

          <div className="text-gray-600">Empresa</div>
          <div className="text-right font-medium break-words">{companyName}</div>
        </div>

        {loading && (
          <div className="mt-4 text-sm text-gray-500">Actualizando datos...</div>
        )}
      </section>

      <section className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-xl font-semibold mb-2">Idioma preferido</h2>
        <p className="text-gray-600 mb-4">
          El soporte multilenguaje estará disponible próximamente.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="border rounded-md px-4 py-2.5 bg-gray-50 text-gray-700 min-w-[220px]">
            ES Español
          </div>
          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-sm font-medium w-fit">
            Coming soon
          </span>
        </div>
      </section>

      <section className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-xl font-semibold mb-2">Contact with us</h2>
        <p className="text-gray-600 mb-5">
          Si necesitas ayuda, puedes contactar con nosotros.
        </p>

        <div className="space-y-5 text-sm md:text-base">
          <div>
            <div className="text-gray-500 mb-1">Teléfono</div>
            <div className="font-medium">+34 651 493 211</div>
          </div>

          <div>
            <div className="text-gray-500 mb-1">Email</div>
            <div className="font-medium break-words">info@cloudtainer.com</div>
          </div>

          <div>
            <div className="text-gray-500 mb-1">Dirección</div>
            <div className="font-medium break-words">
              Av. Sabino Arana 8, Planta 2. Bilbao, Bizkaia - 48013 SPAIN
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}
